// Part Intelligence catalog feedback (Phase 1C): pure model, no database.
//
// Read docs/part-intelligence/FEATURE_CHARTER.md before changing anything here.
//
// RFQ -> broker suspicion -> HUMAN review -> human correction -> export.
// Never: RFQ -> automated catalog change. Nothing in this module, or anything
// built on it, writes the public catalog. The export is a set of instructions
// for a separate, later, human-run catalog process.
//
// Suspicion is evidence for review, not proof. Phase 0 found that half of the
// identifiers that looked wrong were real, rare parts.

import { z } from 'zod';

// ---------------------------------------------------------------------------
// Review states
// ---------------------------------------------------------------------------

/** Values a reviewer can record. `reopened` returns an issue to open. There is
 *  deliberately no machine-set state: only a person writes these. */
export const REVIEW_VALUES = ['needs_evidence', 'confirmed_problem', 'dismissed', 'reopened'] as const;
export type ReviewValue = (typeof REVIEW_VALUES)[number];
/** What the queue shows. `open` = never reviewed, or reopened. */
export type ReviewStatus = 'open' | 'needs_evidence' | 'confirmed_problem' | 'dismissed';

export const REVIEW_STATUS_LABEL: Record<ReviewStatus, string> = {
  open: 'Open',
  needs_evidence: 'Needs more evidence',
  confirmed_problem: 'Confirmed catalog problem',
  dismissed: 'Dismissed, appears valid',
};

/** Structured correction guidance, recorded only with a confirmed problem.
 *  Recommendations for a later catalog process; none is executed here. */
export const CORRECTION_TYPES = [
  'incorrect_identifier',
  'formatting_error',
  'scrape_artifact',
  'seller_as_manufacturer',
  'wrong_source_association',
  'replacement_identifier',
  'review_page_for_removal',
  'other',
] as const;
export type CorrectionType = (typeof CORRECTION_TYPES)[number];

export const CORRECTION_LABEL: Record<CorrectionType, string> = {
  incorrect_identifier: 'Incorrect identifier',
  formatting_error: 'Formatting error',
  scrape_artifact: 'Suspected scrape artifact',
  seller_as_manufacturer: 'Seller shown as manufacturer',
  wrong_source_association: 'Wrong source association',
  replacement_identifier: 'Replace with corrected identifier',
  review_page_for_removal: 'Review page for removal / noindex / redirect',
  other: 'Other',
};

export const MAX_REVIEW_NOTE = 2000;

export const ReviewInput = z
  .object({
    issueKey: z.string().min(3).max(300),
    value: z.enum(REVIEW_VALUES),
    correctionType: z.enum(CORRECTION_TYPES).optional(),
    replacementIdentifier: z.string().trim().min(1).max(100).optional(),
    note: z.string().trim().max(MAX_REVIEW_NOTE).optional(),
  })
  .superRefine((d, ctx) => {
    const confirming = d.value === 'confirmed_problem';
    if (confirming && !d.correctionType) {
      ctx.addIssue({ code: 'custom', path: ['correctionType'], message: 'A confirmed problem needs a correction type' });
    }
    if (!confirming && (d.correctionType || d.replacementIdentifier)) {
      ctx.addIssue({ code: 'custom', path: ['correctionType'], message: 'Correction details belong only to a confirmed problem' });
    }
    if (d.correctionType === 'replacement_identifier' && !d.replacementIdentifier) {
      ctx.addIssue({ code: 'custom', path: ['replacementIdentifier'], message: 'Give the corrected identifier' });
    }
  });
export type ReviewInput = z.infer<typeof ReviewInput>;

export interface ReviewRecord {
  id: string;
  issueKey: string;
  value: ReviewValue;
  correctionType: CorrectionType | null;
  replacementIdentifier: string | null;
  familyId: string | null;
  note: string | null;
  reviewedBy: string | null;
  reviewedAt: string;
  supersedes: string | null;
}

/** Current status from history (newest first or any order). */
export function currentReview(history: ReviewRecord[]): { status: ReviewStatus; record: ReviewRecord | null } {
  let latest: ReviewRecord | null = null;
  for (const r of history) if (!latest || r.reviewedAt > latest.reviewedAt) latest = r;
  if (!latest || latest.value === 'reopened') return { status: 'open', record: latest };
  return { status: latest.value, record: latest };
}

// ---------------------------------------------------------------------------
// Provenance: when may we say an RFQ came from a catalog page?
// ---------------------------------------------------------------------------

/** The catalog page an RFQ came from, or null when that is not established.
 *
 *  Established only when ALL hold:
 *    1. it arrived through the tenant's own web form. Its own catalog posts
 *       carry no `source_site`; partner sites (e.g. honest.aero) declare one,
 *       and their pages are not this tenant's catalog;
 *    2. the intake's raw snapshot names a `/parts/<slug>` page;
 *    3. that slug exists in the catalog (checked by the caller).
 *
 *  Never inferred from the identifier merely existing in the catalog. */
export function catalogSlugFromRfq(rfq: {
  sourceChannel: string | null;
  sourceSite: string | null;
  sourceRawText: string | null;
}): string | null {
  if (rfq.sourceChannel !== 'web_form' || rfq.sourceSite) return null;
  if (!rfq.sourceRawText) return null;
  try {
    const p = (JSON.parse(rfq.sourceRawText) as { sourcePagePath?: unknown })?.sourcePagePath;
    const m = typeof p === 'string' ? /^\/parts\/([A-Za-z0-9._~%-]+)$/.exec(p) : null;
    return m ? m[1]! : null;
  } catch {
    return null;
  }
}

/** One review item per catalog record when the record is known, otherwise per
 *  customer identifier. Stable across requests: it is what reviews attach to. */
export function issueKeyFor(input: { catalogSlug: string | null; normalizedIdentifier: string }): string {
  return input.catalogSlug ? `slug:${input.catalogSlug}` : `pn:${input.normalizedIdentifier}`;
}

// ---------------------------------------------------------------------------
// Families: deterministic REVIEW AIDS from Phase 0, nothing more
// ---------------------------------------------------------------------------

/** A family groups catalog records that MAY share a scraper rule. Membership is
 *  a reason to look, never a verdict. Phase 0 found real parts inside the same
 *  shapes. No code path changes a review status because of a family. */
export interface FamilyRule {
  id: string;
  label: string;
  /** Exactly the Phase 0 discovery rule (docs/part-intelligence-phase-0-discovery.md). */
  description: string;
  /** Postgres predicate over public.parts aliased p. Read-only counting only. */
  sql: string;
  matches: (catalogPartNumber: string, catalogManufacturer: string | null) => boolean;
}

export const FAMILY_RULES: readonly FamilyRule[] = [
  {
    id: 'kt_prefix_turbine_kinetics',
    label: 'KT-prefixed, Turbine Kinetics',
    description: "Part number starts with KT and a digit; catalog manufacturer starts with 'Turbine Kinetics'.",
    sql: "p.part_number ~ '^KT[0-9]' AND p.manufacturer ILIKE 'Turbine Kinetics%'",
    matches: (pn, mfr) => /^KT[0-9]/.test(pn) && /^turbine kinetics/i.test(mfr ?? ''),
  },
  {
    id: 'aar_suffix',
    label: 'Ends in AAR',
    description: 'Part number ends with the letters AAR.',
    sql: "p.part_number LIKE '%AAR'",
    matches: (pn) => pn.endsWith('AAR'),
  },
  {
    id: 'chi_prefix',
    label: 'CHI- prefix',
    description: 'Part number starts with CHI- (case-insensitive).',
    sql: "p.part_number ILIKE 'CHI-%'",
    matches: (pn) => /^chi-/i.test(pn),
  },
  {
    id: 'pc12_sur',
    label: 'PC12-SUR-',
    description: 'Part number starts with PC12-SUR- (case-insensitive).',
    sql: "p.part_number ILIKE 'PC12-SUR-%'",
    matches: (pn) => /^pc12-sur-/i.test(pn),
  },
  {
    id: 'ev_infix',
    label: 'EV vendor infix',
    description: 'A digit, then EV-, then a digit (as in 119A1590EV-606).',
    sql: "p.part_number ~ '[0-9]EV-[0-9]'",
    matches: (pn) => /[0-9]EV-[0-9]/.test(pn),
  },
  {
    id: 'contains_space',
    label: 'Contains a space',
    description: 'Part number contains a space. Very common, and often legitimate.',
    sql: "p.part_number LIKE '% %'",
    matches: (pn) => pn.includes(' '),
  },
];

export function familiesFor(catalogPartNumber: string | null, catalogManufacturer: string | null): string[] {
  if (!catalogPartNumber) return [];
  return FAMILY_RULES.filter((f) => f.matches(catalogPartNumber, catalogManufacturer)).map((f) => f.id);
}

// ---------------------------------------------------------------------------
// Export: human-reviewed corrections, minimal, deterministic
// ---------------------------------------------------------------------------

export const EXPORT_FORMAT = 'apd.catalog-corrections' as const;
export const EXPORT_VERSION = 1 as const;

/** Everything the catalog process needs, and nothing it does not: no customer,
 *  contact, RFQ, quote, vendor or PartsBase data. */
export interface ExportItem {
  reviewId: string;
  issueKey: string;
  catalog: { slug: string; path: string; partNumber: string | null } | null;
  identifier: string;
  decision: 'confirmed_problem';
  correctionType: CorrectionType;
  replacementIdentifier: string | null;
  familyId: string | null;
  reviewedBy: string | null;
  reviewedAt: string;
  note: string | null;
  evidence: {
    flisEdition: string | null;
    flisState: string | null;
    machineStateWhenFlagged: string | null;
    timesFlagged: number;
    rfqCount: number;
    firstObserved: string | null;
    lastObserved: string | null;
  };
}

export interface CorrectionExport {
  format: typeof EXPORT_FORMAT;
  version: typeof EXPORT_VERSION;
  /** SHA-256 of `items` as serialized here: equal for equal content. */
  contentSha256: string;
  itemCount: number;
  exportedAt: string;
  items: ExportItem[];
}

// ---------------------------------------------------------------------------
// Queue shapes (shared by server and client)
// ---------------------------------------------------------------------------

export interface QueueRfq {
  rfqId: string; ref: string; lineItemId: string;
  /** The identifier when the broker flagged it. */
  entered: string;
  /** The line's part number now; differs when a broker edited the line afterwards. */
  lineNow: string;
  flaggedAt: string;
}
export interface QueueItem {
  issueKey: string;
  /** Conservative lookup key of what customers entered. */
  identifier: string;
  /** Distinct identifiers as they stood when flagged (not the line's later edits). */
  entered: string[];
  /** Established catalog record, or null ("Catalog source not established"). */
  catalog: { slug: string; path: string; partNumber: string; manufacturer: string | null } | null;
  flagCount: number;
  rfqCount: number;
  rfqs: QueueRfq[];
  firstFlagged: string | null;
  lastFlagged: string | null;
  flaggedBy: string[];
  notes: string[];
  /** From the most recent flag's stored snapshot. */
  evidence: {
    machineState: string | null;
    flisState: string | null;
    flisEdition: string | null;
    partsbaseState: string | null;
    partsbaseBasis: string | null;
    candidate: string | null;
  };
  families: string[];
  status: ReviewStatus;
  review: ReviewRecord | null;
  history: ReviewRecord[];
}

export interface FamilySummary {
  id: string; label: string; description: string;
  catalogCount: number | null;
  samples: Array<{ partNumber: string; path: string }>;
}
